import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { apiCall } from '../utils/api';
import { useToast } from '../hooks/use-toast';
import { 
  TrendingUp, TrendingDown, AlertTriangle, MapPin, Calendar, 
  RefreshCw, ChevronLeft, Zap, Target, Lightbulb, BarChart3, Shield
} from 'lucide-react';

const HazardTrendAnalysis = ({ onClose }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('30');
  const [analyzing, setAnalyzing] = useState(false);
  const { toast } = useToast();

  const fetchAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiCall(`/api/ai-hazard-scan/trend-analysis?days=${period}`);
      setAnalysis(data);
    } catch (err) {
      console.error('Failed to fetch trend analysis:', err);
      setError(err.message);
      toast({ title: 'Load Failed', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [period, toast]);

  const runAIAnalysis = useCallback(async () => {
    setAnalyzing(true);
    try {
      const data = await apiCall(`/api/ai-hazard-scan/ai-trend-analysis?days=${period}`, {
        method: 'POST'
      });
      setAnalysis(prev => ({ ...prev, ai_insights: data.insights }));
      toast({ title: 'Analysis Complete', description: 'AI has generated new insights' });
    } catch (err) {
      console.error('AI analysis failed:', err);
      toast({ title: 'Analysis Failed', description: err.message, variant: 'destructive' });
    } finally {
      setAnalyzing(false);
    }
  }, [period, toast]);

  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);

  const getTrendIcon = (trend) => {
    if (trend > 0) return <TrendingUp className="w-4 h-4 text-red-500" />;
    if (trend < 0) return <TrendingDown className="w-4 h-4 text-green-500" />;
    return null;
  };

  const getRiskColor = (risk) => {
    const colors = {
      critical: 'bg-red-500',
      high: 'bg-orange-500',
      medium: 'bg-yellow-500',
      low: 'bg-green-500'
    };
    return colors[risk] || 'bg-gray-500';
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                Hazard Trend Analysis
              </CardTitle>
              <CardDescription>AI-powered insights into safety patterns</CardDescription>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-36">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="180">Last 6 months</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="default" 
              size="sm" 
              onClick={runAIAnalysis}
              disabled={analyzing || loading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {analyzing ? (
                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-1" />
              )}
              AI Analysis
            </Button>
            
            <Button variant="outline" size="sm" onClick={fetchAnalysis} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 overflow-auto">
        {error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-2" />
              <p className="text-gray-600">{error}</p>
              <Button variant="outline" className="mt-4" onClick={fetchAnalysis}>
                Retry
              </Button>
            </div>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : analysis ? (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{analysis.total_scans}</p>
                      <p className="text-sm text-gray-600">Total Scans</p>
                    </div>
                    {getTrendIcon(analysis.scan_trend)}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold text-orange-600">{analysis.total_hazards}</p>
                      <p className="text-sm text-gray-600">Total Hazards</p>
                    </div>
                    {getTrendIcon(analysis.hazard_trend)}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-4">
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{analysis.avg_hazards?.toFixed(1)}</p>
                    <p className="text-sm text-gray-600">Avg per Scan</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-4">
                  <div>
                    <p className="text-2xl font-bold text-green-600">{analysis.clean_scan_rate}%</p>
                    <p className="text-sm text-gray-600">Clean Scan Rate</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Risk Distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  Risk Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {['critical', 'high', 'medium', 'low'].map(risk => {
                    const count = analysis.risk_distribution?.[risk] || 0;
                    const percentage = analysis.total_scans > 0 
                      ? Math.round((count / analysis.total_scans) * 100) 
                      : 0;
                    return (
                      <div key={risk} className="flex items-center gap-3">
                        <span className="w-20 text-sm capitalize font-medium">{risk}</span>
                        <Progress value={percentage} className="flex-1" />
                        <span className="w-16 text-sm text-gray-600 text-right">
                          {count} ({percentage}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            
            {/* Top Hazard Types */}
            {analysis.top_hazard_types?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                    Top Hazard Types
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {analysis.top_hazard_types.map((hazard, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="font-medium">{hazard.type}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{hazard.count} occurrences</Badge>
                          {hazard.trend > 0 && <TrendingUp className="w-4 h-4 text-red-500" />}
                          {hazard.trend < 0 && <TrendingDown className="w-4 h-4 text-green-500" />}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* High-Risk Locations */}
            {analysis.high_risk_locations?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-red-600" />
                    High-Risk Locations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {analysis.high_risk_locations.map((loc, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-red-50 rounded">
                        <div>
                          <span className="font-medium">{loc.location}</span>
                          <p className="text-xs text-gray-500">{loc.project_name || 'Multiple projects'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-red-100 text-red-700">{loc.hazard_count} hazards</Badge>
                          <Badge className={`${getRiskColor(loc.max_risk)} text-white`}>
                            {loc.max_risk?.toUpperCase()}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* AI Insights */}
            {analysis.ai_insights && (
              <Card className="border-purple-200 bg-purple-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-purple-600" />
                    AI-Generated Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analysis.ai_insights.patterns && (
                      <div>
                        <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                          <Target className="w-4 h-4" />
                          Identified Patterns
                        </h4>
                        <ul className="space-y-1">
                          {analysis.ai_insights.patterns.map((pattern, i) => (
                            <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                              <span className="text-purple-500 mt-1">•</span>
                              {pattern}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {analysis.ai_insights.recommendations && (
                      <div>
                        <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                          <Zap className="w-4 h-4" />
                          Recommendations
                        </h4>
                        <ul className="space-y-1">
                          {analysis.ai_insights.recommendations.map((rec, i) => (
                            <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                              <span className="text-purple-500 mt-1">•</span>
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {analysis.ai_insights.risk_forecast && (
                      <div className="mt-4 p-3 bg-white rounded-lg border border-purple-200">
                        <h4 className="font-medium text-purple-800 mb-1">Risk Forecast</h4>
                        <p className="text-sm text-gray-700">{analysis.ai_insights.risk_forecast}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {!analysis.ai_insights && (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Lightbulb className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">Click "AI Analysis" to generate insights</p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No data available</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HazardTrendAnalysis;
